import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Share2, Play, ArrowLeft, Send, X, Volume2, VolumeX } from 'lucide-react';
import { postAPI } from '../services/api';
import { getCurrentUser } from '../utils/auth';
import { toast } from 'sonner';

const Explore = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Modes: null = grid, 'feed' = photo feed, 'reels' = video reels
  const [mode, setMode] = useState(null);
  const [startIndex, setStartIndex] = useState(0);
  const [commentOpenId, setCommentOpenId] = useState(null);
  const [commentTexts, setCommentTexts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [muted, setMuted] = useState(true);
  const currentUser = getCurrentUser();
  const observerRef = useRef();
  const postRefs = useRef({});
  const videoRefs = useRef({});
  const reelScrollRef = useRef();

  useEffect(() => { fetchPosts(1); }, []);

  const fetchPosts = async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      const response = await postAPI.getExplore(pageNum, 30);
      const data = response.data;
      const newPosts = data.posts || [];
      if (pageNum === 1) setPosts(newPosts);
      else setPosts(prev => [...prev, ...newPosts]);
      setHasMore(data.hasMore);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching explore:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const lastPostRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) fetchPosts(page + 1);
    });
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, page]);

  const getThumb = (post) => (post.mediaUrls || [])[0] || '';
  const isVideo = (url) => {
    if (!url) return false;
    const l = url.toLowerCase();
    return l.includes('.mp4') || l.includes('.mov') || l.includes('.webm') || l.includes('video');
  };
  const timeAgo = (date) => {
    if (!date) return '';
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 604800) return `${Math.floor(s / 86400)}d`;
    return `${Math.floor(s / 604800)}w`;
  };

  // Get only video posts
  const videoPosts = posts.filter(p => isVideo(getThumb(p)));

  // Open correct mode based on media type
  const openPost = (idx) => {
    const post = posts[idx];
    const thumb = getThumb(post);
    setCommentOpenId(null);
    setCommentTexts({});

    if (isVideo(thumb)) {
      // Find this post's index in the video-only array
      const videoIdx = videoPosts.findIndex(p => p._id === post._id);
      setMode('reels');
      setStartIndex(videoIdx >= 0 ? videoIdx : 0);
      setTimeout(() => {
        const el = postRefs.current[`reel-${videoIdx >= 0 ? videoIdx : 0}`];
        if (el) el.scrollIntoView({ behavior: 'instant' });
      }, 50);
    } else {
      setMode('feed');
      setStartIndex(idx);
      setTimeout(() => {
        const el = postRefs.current[`feed-${idx}`];
        if (el) el.scrollIntoView({ behavior: 'instant' });
      }, 50);
    }
  };

  const closeMode = () => {
    setMode(null);
    setCommentOpenId(null);
    // Pause all videos
    Object.values(videoRefs.current).forEach(v => { if (v) v.pause(); });
  };

  // Autoplay visible reel video
  useEffect(() => {
    if (mode !== 'reels' || !reelScrollRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.6 });
    const items = reelScrollRef.current.querySelectorAll('[data-reel-item]');
    items.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, [mode, videoPosts.length]);

  const handleLike = async (postId) => {
    const post = posts.find(p => p._id === postId);
    if (!post) return;
    try {
      const isLiked = post.liked;
      if (isLiked) await postAPI.unlikePost(postId);
      else await postAPI.likePost(postId);
      setPosts(prev => prev.map(p => p._id === postId ? {
        ...p, liked: !isLiked,
        likes: isLiked
          ? (p.likes || []).filter(id => id !== currentUser?._id)
          : [...(p.likes || []), currentUser?._id]
      } : p));
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleComment = async (postId) => {
    const text = (commentTexts[postId] || '').trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const res = await postAPI.addComment(postId, text);
      const newComment = res.data.comment;
      setPosts(prev => prev.map(p => p._id === postId ? {
        ...p, comments: [...(p.comments || []), newComment]
      } : p));
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async (postId) => {
    const url = `${window.location.origin}/home?post=${postId}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Check out this post', url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  // Arrange grid: every 4th item (positions 3, 7, 11...) is a video
  const gridPosts = (() => {
    const photos = posts.filter(p => !isVideo(getThumb(p)));
    const videos = posts.filter(p => isVideo(getThumb(p)));
    const arranged = [];
    let pi = 0, vi = 0;
    while (pi < photos.length || vi < videos.length) {
      // 3 photos then 1 video
      if (pi < photos.length) arranged.push(photos[pi++]);
      if (pi < photos.length) arranged.push(photos[pi++]);
      if (pi < photos.length) arranged.push(photos[pi++]);
      if (vi < videos.length) arranged.push(videos[vi++]);
      else if (pi < photos.length) arranged.push(photos[pi++]);
    }
    return arranged;
  })();

  // Map grid index back to original posts index for openPost
  const gridToOriginal = (gridPost) => posts.findIndex(p => p._id === gridPost._id);

  // =================== GRID VIEW ===================
  if (mode === null) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="max-w-3xl mx-auto px-0.5 pt-16 md:pt-20">
          {loading ? (
            <div className="grid grid-cols-3 gap-0.5" data-testid="explore-skeleton">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full" />
              ))}
            </div>
          ) : gridPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center px-6">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-semibold text-foreground mb-1">No media yet</p>
              <p className="text-sm text-muted-foreground">Posts with photos and videos will appear here</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-0.5 skeleton-fade-in" data-testid="explore-grid">
              {gridPosts.map((post, idx) => {
                const thumb = getThumb(post);
                const video = isVideo(thumb);
                const isLast = idx === gridPosts.length - 1;
                return (
                  <div
                    key={post._id}
                    ref={isLast ? lastPostRef : null}
                    className="aspect-square relative cursor-pointer group overflow-hidden bg-muted"
                    onClick={() => openPost(gridToOriginal(post))}
                    data-testid={`explore-item-${idx}`}
                  >
                    {video ? (
                      <div className="w-full h-full relative">
                        <video src={thumb} className="w-full h-full object-cover" muted autoPlay loop playsInline preload="metadata" />
                        <div className="absolute top-2 right-2">
                          <Play className="w-5 h-5 text-white drop-shadow-lg" fill="white" />
                        </div>
                      </div>
                    ) : (
                      <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex items-center gap-4 text-white text-sm font-semibold">
                        <span className="flex items-center gap-1"><Heart className="w-5 h-5" fill="white" /> {(post.likes || []).length}</span>
                        <span className="flex items-center gap-1"><MessageCircle className="w-5 h-5" fill="white" /> {(post.comments || []).length}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {loadingMore && (
            <div className="grid grid-cols-3 gap-0.5 mt-0.5">
              {[0, 1, 2].map(i => <Skeleton key={i} className="aspect-square w-full" />)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // =================== PHOTO FEED (Instagram-style cards) ===================
  if (mode === 'feed') {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <div className="fixed top-0 left-0 right-0 z-50 h-12 flex items-center px-4 bg-background border-b border-border/40" data-testid="explore-feed-header">
          <button onClick={closeMode} className="flex items-center gap-2 text-foreground" data-testid="explore-feed-back">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-base font-semibold">Explore</span>
          </button>
        </div>

        <div className="pt-12 overflow-y-auto" data-testid="explore-feed-scroll">
          {posts.map((post, idx) => {
            const thumb = getThumb(post);
            if (isVideo(thumb)) return null; // Skip videos in photo feed
            const liked = post.liked;
            const likeCount = (post.likes || []).length;
            const commentCount = (post.comments || []).length;
            const isCommentOpen = commentOpenId === post._id;

            return (
              <div
                key={post._id}
                ref={el => { postRefs.current[`feed-${idx}`] = el; }}
                className="border-b border-border/30"
                data-testid={`explore-feed-item-${idx}`}
              >
                {/* User header */}
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center space-x-3">
                    <button onClick={() => navigate(`/profile/${post.user?.username}`)}>
                      <Avatar className="w-10 h-10 hover:scale-105 transition-transform ring-2 ring-transparent hover:ring-purple-200">
                        <AvatarImage src={post.user?.profileImage} />
                        <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold">{post.user?.name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                    </button>
                    <div>
                      <button onClick={() => navigate(`/profile/${post.user?.username}`)} className="hover:opacity-80 transition-opacity">
                        <p className="font-semibold text-sm text-foreground leading-tight">{post.user?.name}</p>
                      </button>
                      <div className="flex items-center space-x-1.5 mt-0.5">
                        <p className="text-xs text-muted-foreground">@{post.user?.username}</p>
                        {post.user?.persona && (
                          <>
                            <span className="text-muted-foreground text-xs">·</span>
                            <span className="text-[10px] px-2 py-0 rounded-full bg-primary/10 text-primary font-medium capitalize">{post.user.persona}</span>
                          </>
                        )}
                        <span className="text-muted-foreground text-xs">· {timeAgo(post.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Media */}
                <div className="w-full bg-muted/20">
                  <img src={thumb} alt="" className="w-full" loading="lazy" />
                </div>

                {/* Actions */}
                <div className="px-5 py-3">
                  <div className="flex items-center space-x-6">
                    <button onClick={() => handleLike(post._id)} className="flex items-center space-x-1.5 group transition-all" data-testid={`explore-feed-like-${idx}`}>
                      <Heart className={`w-5 h-5 transition-all group-hover:scale-110 ${liked ? 'fill-primary text-primary' : 'text-muted-foreground/70 group-hover:text-primary'}`} />
                      <span className={`text-sm ${liked ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{likeCount.toLocaleString()}</span>
                    </button>
                    <button onClick={() => setCommentOpenId(isCommentOpen ? null : post._id)} className="flex items-center space-x-1.5 group transition-all" data-testid={`explore-feed-comment-${idx}`}>
                      <MessageCircle className="w-5 h-5 text-muted-foreground/70 group-hover:text-primary transition-colors group-hover:scale-110" />
                      <span className="text-sm text-muted-foreground">{commentCount}</span>
                    </button>
                    <button onClick={() => handleShare(post._id)} className="flex items-center space-x-1.5 group transition-all" data-testid={`explore-feed-share-${idx}`}>
                      <Share2 className="w-5 h-5 text-muted-foreground/70 group-hover:text-primary transition-colors group-hover:scale-110" />
                      <span className="text-sm text-muted-foreground">Share</span>
                    </button>
                  </div>
                </div>

                {likeCount > 0 && (
                  <div className="px-5 pt-1">
                    <span className="text-sm font-semibold text-foreground">{likeCount.toLocaleString()} {likeCount === 1 ? 'like' : 'likes'}</span>
                  </div>
                )}

                {post.content && (
                  <div className="px-5 pt-1 pb-1">
                    <p className="text-foreground text-[15px] leading-relaxed whitespace-pre-wrap">{post.content}</p>
                  </div>
                )}

                {commentCount > 0 && !isCommentOpen && (
                  <button onClick={() => setCommentOpenId(post._id)} className="px-5 pt-0.5 pb-2">
                    <span className="text-muted-foreground text-sm">View all {commentCount} comments</span>
                  </button>
                )}

                {isCommentOpen && (
                  <div className="px-5 pb-3 border-t border-border/50 mt-2 pt-4" data-testid={`explore-feed-comments-panel-${idx}`}>
                    <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
                      {commentCount === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-4">No comments yet</p>
                      ) : (
                        (post.comments || []).map((c) => (
                          <div key={c._id} className="flex gap-2.5">
                            <Avatar className="w-7 h-7 flex-shrink-0">
                              <AvatarImage src={c.user?.profileImage} />
                              <AvatarFallback className="text-[10px]">{c.user?.name?.[0] || '?'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="text-sm">
                                <span className="font-semibold text-foreground mr-1">{c.user?.name}</span>
                                <span className="text-foreground/80">{c.text}</span>
                              </p>
                              <span className="text-muted-foreground text-[10px]">{timeAgo(c.createdAt)}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex items-center gap-2 border-t border-border/50 pt-4">
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={commentTexts[post._id] || ''}
                        onChange={(e) => setCommentTexts(prev => ({ ...prev, [post._id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleComment(post._id)}
                        className="flex-1 text-sm outline-none text-foreground placeholder:text-muted-foreground bg-transparent"
                        data-testid={`explore-feed-comment-input-${idx}`}
                      />
                      <button onClick={() => handleComment(post._id)} disabled={!(commentTexts[post._id] || '').trim() || submitting} className="text-primary font-semibold text-sm disabled:opacity-30">
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // =================== VIDEO REELS (Full-screen vertical snap) ===================
  return (
    <div className="min-h-screen bg-black pb-16 md:pb-0">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-11 flex items-center justify-between px-4" data-testid="explore-reels-header">
        <button onClick={closeMode} className="flex items-center gap-2 text-white drop-shadow-lg" data-testid="explore-reels-back">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-semibold">Media</span>
        </button>
        <button
          onClick={() => setMuted(!muted)}
          className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white"
          data-testid="explore-reels-mute"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Snap-scroll reel feed */}
      <div
        ref={reelScrollRef}
        className="overflow-y-auto snap-y snap-mandatory"
        style={{ height: 'calc(100vh - 64px)' }}
        data-testid="explore-reels-scroll"
      >
        {videoPosts.map((post, idx) => {
          const thumb = getThumb(post);
          const liked = post.liked;
          const likeCount = (post.likes || []).length;
          const commentCount = (post.comments || []).length;
          const isCommentOpen = commentOpenId === post._id;

          return (
            <div
              key={post._id}
              ref={el => { postRefs.current[`reel-${idx}`] = el; }}
              data-reel-item
              className="snap-start relative bg-black"
              style={{ height: 'calc(100vh - 64px)' }}
              data-testid={`explore-reel-item-${idx}`}
            >
              {/* Video — fills the container */}
              <video
                ref={el => { videoRefs.current[idx] = el; }}
                src={thumb}
                className="absolute inset-0 w-full h-full object-contain"
                loop
                playsInline
                muted={muted}
                preload="auto"
              />

              {/* Gradient for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

              {/* Right side vertical actions */}
              <div className="absolute right-3 bottom-8 flex flex-col items-center gap-5 z-10">
                <button onClick={() => handleLike(post._id)} className="flex flex-col items-center gap-1 active:scale-125 transition-transform" data-testid={`explore-reel-like-${idx}`}>
                  <Heart className={`w-5 h-5 ${liked ? 'text-red-500 fill-red-500' : 'text-white'}`} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                  <span className="text-white text-[10px] font-semibold" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{likeCount}</span>
                </button>

                <button onClick={() => setCommentOpenId(isCommentOpen ? null : post._id)} className="flex flex-col items-center gap-1 active:scale-110 transition-transform" data-testid={`explore-reel-comment-${idx}`}>
                  <MessageCircle className="w-5 h-5 text-white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                  <span className="text-white text-[10px] font-semibold" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{commentCount}</span>
                </button>

                <button onClick={() => handleShare(post._id)} className="flex flex-col items-center gap-1 active:scale-110 transition-transform" data-testid={`explore-reel-share-${idx}`}>
                  <Share2 className="w-5 h-5 text-white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                </button>
              </div>

              {/* Bottom left — avatar + user info + caption */}
              <div className="absolute bottom-6 left-4 right-16 z-10">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <button onClick={() => navigate(`/profile/${post.user?.username}`)}>
                    <Avatar className="w-9 h-9 ring-2 ring-white/30">
                      <AvatarImage src={post.user?.profileImage} />
                      <AvatarFallback className="bg-white/20 text-white text-xs font-semibold">{post.user?.name?.[0] || '?'}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => navigate(`/profile/${post.user?.username}`)}>
                        <span className="text-white font-bold text-[14px]" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>{post.user?.name}</span>
                      </button>
                      {post.user?.persona && (
                        <span className="px-1.5 py-0.5 rounded-full bg-white/15 text-white/80 text-[10px] font-medium backdrop-blur-sm">{post.user.persona}</span>
                      )}
                    </div>
                  </div>
                </div>
                {post.content && (
                  <p className="text-white/90 text-[13px] leading-snug line-clamp-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{post.content}</p>
                )}
              </div>

              {/* Comments panel */}
              {isCommentOpen && (
                <div className="absolute bottom-0 left-0 right-0 bg-card/95 backdrop-blur-xl rounded-t-2xl z-40 max-h-[55vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={`explore-reel-comments-panel-${idx}`}>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
                    <span className="font-semibold text-foreground text-sm">Comments ({commentCount})</span>
                    <button onClick={() => setCommentOpenId(null)}><X className="w-5 h-5 text-muted-foreground" /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3.5 max-h-[38vh]">
                    {commentCount === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-8">No comments yet. Be the first!</p>
                    ) : (
                      (post.comments || []).map((c) => (
                        <div key={c._id} className="flex gap-2.5">
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarImage src={c.user?.profileImage} />
                            <AvatarFallback className="text-[10px]">{c.user?.name?.[0] || '?'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-baseline gap-1.5">
                              <span className="font-semibold text-xs text-foreground">{c.user?.name}</span>
                              <span className="text-muted-foreground text-[10px]">{timeAgo(c.createdAt)}</span>
                            </div>
                            <p className="text-sm text-foreground/80 mt-0.5">{c.text}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-2 p-3 border-t border-border/30">
                    <input
                      type="text"
                      placeholder="Add a comment..."
                      value={commentTexts[post._id] || ''}
                      onChange={(e) => setCommentTexts(prev => ({ ...prev, [post._id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleComment(post._id)}
                      className="flex-1 bg-muted rounded-full px-3.5 py-2 text-sm outline-none text-foreground placeholder:text-muted-foreground"
                      data-testid={`explore-reel-comment-input-${idx}`}
                    />
                    <button onClick={() => handleComment(post._id)} disabled={!(commentTexts[post._id] || '').trim() || submitting} className="text-primary disabled:opacity-30">
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Explore;
